import i18n from "i18next";
import type { TFunction } from "i18next";
import { initReactI18next } from "react-i18next";
import { en } from "./locales/en";
import { zhCN } from "./locales/zh-CN";

/**
 * Translation function signature used by presentation helpers. It matches the
 * react-i18next `t` so page helpers can be typed without importing i18next.
 */
export type Translate = TFunction;

export const SUPPORTED_LOCALES = ["en", "zh-CN"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en";
export const LOCALE_STORAGE_KEY = "mbip.locale";

const RESOURCES = {
  en: { translation: en },
  "zh-CN": { translation: zhCN },
} as const;

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return value === "en" || value === "zh-CN";
}

export function readStoredLocale(): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function persistLocale(locale: SupportedLocale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable (private mode); in-memory locale still works.
  }
}

export function changeLocale(locale: SupportedLocale): void {
  persistLocale(locale);
  void i18n.changeLanguage(locale);
}

void i18n.use(initReactI18next).init({
  resources: RESOURCES,
  lng: readStoredLocale(),
  fallbackLng: "en",
  supportedLngs: ["en", "zh-CN"],
  nonExplicitSupportedLngs: false,
  load: "currentOnly",
  interpolation: { escapeValue: false },
  returnNull: false,
  // A missing key falls back to English via fallbackLng. Keys missing in BOTH
  // locales resolve to the semantic key itself; the console warning keeps
  // development honest while production UI still renders a deterministic value.
  missingKeyHandler: (_lng, _ns, key) => {
    if (import.meta.env?.DEV) {
      console.warn(`[mbip-i18n] missing translation key: ${String(key)}`);
    }
  },
});

/**
 * Maps the active i18next locale to the Intl locale used for presentation
 * formatting. English keeps the existing en-GB grouping/date convention so
 * the en presentation contract is byte-identical to the pre-i18n build.
 */
export function intlLocale(): string {
  return i18n.language === "zh-CN" ? "zh-CN" : "en-GB";
}

export function formatDateLabel(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const normalized = value.length >= 10 ? value.slice(0, 10) : value;
  const parsed = new Date(`${normalized}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(intlLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

// Backend state codes are frozen business enums and are never re-encoded;
// only their rendered label is localized. Codes without a mapping keep the
// humanized English form (e.g. "FX_CONVERSION_REQUIRED" → "FX CONVERSION
// REQUIRED") so the label never disappears.
const BACKEND_STATE_KEYS: Readonly<Record<string, string>> = {
  DERIVED: "status.derived",
  MISSING: "status.missing",
  UNRESOLVED: "status.unresolved",
  AVAILABLE: "status.available",
  COMPLETE: "status.complete",
  PARTIAL: "status.partial",
  APPROXIMATE: "status.approximate",
  STALE: "status.stale",
  BLOCKED: "status.blocked",
  RECONCILED: "status.reconciled",
  MATCHED: "status.matched",
  EXACT: "status.exact",
  HEALTHY: "status.healthy",
  CALCULATED: "status.calculated",
  LOW: "status.low",
  HIGH: "status.high",
  EXCESS: "status.excess",
  CRITICAL: "status.critical",
  SOURCE: "status.source",
  FREE_OF_CHARGE: "status.freeOfCharge",
  ZERO: "status.zero",
};

/**
 * Localizes a backend state code for inline Tag rendering. The code itself
 * stays untouched; only the display label switches with the locale.
 */
export function backendStateLabel(code: string): string {
  const key = BACKEND_STATE_KEYS[code];
  if (!key) return code.replaceAll("_", " ");
  return i18n.t(key, code.replaceAll("_", " "));
}

export default i18n;
